import { CombustionProfileId, EngineCategory, EngineProfileConfig, EngineProfileId, EVProfileId } from '../types';
import {
  BMW_I6_M_DCT_ARTIFICIAL_PROFILE,
  BMW_M340I_ZF8HP_PROFILE,
  BMW_S65_V8_DCT_ARTIFICIAL_PROFILE,
  BMW_V10_SMG_ARTIFICIAL_PROFILE,
  LAMBORGHINI_REVUELTO_DCT_PROFILE,
} from './transmissions';

export const ENGINE_PROFILES: Record<CombustionProfileId, EngineProfileConfig> = {
  // -------------------------------------------------------------
  // CLASSIC 4 PROFILES (Preserved & Enhanced)
  // -------------------------------------------------------------
  v8: {
    id: 'v8',
    name: '4.0L Twin-Turbo V8',
    tagline: 'Deep muscular crossplane rumble',
    cylinders: 8,
    firingCharacteristics: 'crossplane',
    idleRpm: 750,
    redlineRpm: 6800,
    maxRpm: 7200,
    gearRatios: BMW_M340I_ZF8HP_PROFILE.gearRatios,
    finalDriveRatio: BMW_M340I_ZF8HP_PROFILE.finalDriveRatio,
    wheelCircumference: BMW_M340I_ZF8HP_PROFILE.wheelCircumference,
    transmissionProfile: BMW_M340I_ZF8HP_PROFILE,
    harmonicRatios: [0.5, 1.0, 1.5, 2.0, 3.0, 4.0, 6.0],
    harmonicLevels: [0.85, 0.90, 0.55, 0.80, 0.50, 0.40, 0.20],
    harmonicDetunes: [-5, 0, 4, -3, 2, 0, -2],
    filterCharacteristics: {
      idleCutoffHz: 360,
      redlineCutoffHz: 2800,
      resonanceQ: 2.4,
      manifoldResonanceHz: 155,
      exhaustThroatHz: 460,
      highFreqRaspHz: 1800,
    },
    distortionAmount: 32,
    intakeIntensity: 0.36,
    exhaustIntensity: 0.45,
    mechanicalNoise: 0.04,
    lowFreqBodyGain: 0.85,
    highFreqCharacterGain: 0.35,
    accelerationResponse: 0.85,
    engineBrakingResponse: 0.80,
    compressionRatioDescription: '10.5:1 High Output Crossplane',
  },

  v6: {
    id: 'v6',
    name: '3.0L Twin-Turbo V6',
    tagline: 'Crisp, raspy sport exhaust note',
    cylinders: 6,
    firingCharacteristics: 'flatplane',
    idleRpm: 800,
    redlineRpm: 7000,
    maxRpm: 7400,
    gearRatios: BMW_M340I_ZF8HP_PROFILE.gearRatios,
    finalDriveRatio: BMW_M340I_ZF8HP_PROFILE.finalDriveRatio,
    wheelCircumference: BMW_M340I_ZF8HP_PROFILE.wheelCircumference,
    transmissionProfile: BMW_M340I_ZF8HP_PROFILE,
    harmonicRatios: [0.5, 1.0, 1.5, 2.0, 3.0, 4.5, 6.0],
    harmonicLevels: [0.55, 0.85, 0.30, 0.75, 0.70, 0.40, 0.30],
    harmonicDetunes: [-2, 0, 1, 0, -1, 2, 0],
    filterCharacteristics: {
      idleCutoffHz: 440,
      redlineCutoffHz: 3400,
      resonanceQ: 2.8,
      manifoldResonanceHz: 180,
      exhaustThroatHz: 520,
      highFreqRaspHz: 2200,
    },
    distortionAmount: 26,
    intakeIntensity: 0.30,
    exhaustIntensity: 0.38,
    mechanicalNoise: 0.045,
    lowFreqBodyGain: 0.60,
    highFreqCharacterGain: 0.50,
    accelerationResponse: 0.88,
    engineBrakingResponse: 0.75,
    compressionRatioDescription: '10.2:1 Twin-Scroll 60-Deg V6',
  },

  i4: {
    id: 'i4',
    name: '2.0L Turbo Inline-4',
    tagline: 'High-strung revving sport compact',
    cylinders: 4,
    firingCharacteristics: 'inline_even',
    idleRpm: 850,
    redlineRpm: 7200,
    maxRpm: 7600,
    gearRatios: BMW_M340I_ZF8HP_PROFILE.gearRatios,
    finalDriveRatio: BMW_M340I_ZF8HP_PROFILE.finalDriveRatio,
    wheelCircumference: BMW_M340I_ZF8HP_PROFILE.wheelCircumference,
    transmissionProfile: BMW_M340I_ZF8HP_PROFILE,
    harmonicRatios: [0.5, 1.0, 2.0, 3.0, 4.0, 5.0],
    harmonicLevels: [0.45, 0.80, 0.85, 0.40, 0.50, 0.25],
    harmonicDetunes: [0, 0, 0, 0, 0, 0],
    filterCharacteristics: {
      idleCutoffHz: 480,
      redlineCutoffHz: 3800,
      resonanceQ: 2.0,
      manifoldResonanceHz: 200,
      exhaustThroatHz: 580,
      highFreqRaspHz: 2500,
    },
    distortionAmount: 22,
    intakeIntensity: 0.38,
    exhaustIntensity: 0.34,
    mechanicalNoise: 0.05,
    lowFreqBodyGain: 0.45,
    highFreqCharacterGain: 0.45,
    accelerationResponse: 0.90,
    engineBrakingResponse: 0.70,
    compressionRatioDescription: '9.8:1 High-Boost Inline-4',
  },

  flat6: {
    id: 'flat6',
    name: '4.0L Naturally Aspirated Flat-6',
    tagline: 'High-revving metallic howl',
    cylinders: 6,
    firingCharacteristics: 'boxer',
    idleRpm: 800,
    redlineRpm: 8400,
    maxRpm: 8800,
    gearRatios: BMW_M340I_ZF8HP_PROFILE.gearRatios,
    finalDriveRatio: BMW_M340I_ZF8HP_PROFILE.finalDriveRatio,
    wheelCircumference: BMW_M340I_ZF8HP_PROFILE.wheelCircumference,
    transmissionProfile: BMW_M340I_ZF8HP_PROFILE,
    harmonicRatios: [0.5, 1.0, 1.5, 2.0, 3.0, 4.0, 6.0, 8.0],
    harmonicLevels: [0.50, 0.85, 0.40, 0.80, 0.80, 0.55, 0.50, 0.30],
    harmonicDetunes: [-1, 0, 2, -2, 1, 0, -1, 1],
    filterCharacteristics: {
      idleCutoffHz: 520,
      redlineCutoffHz: 4800,
      resonanceQ: 3.6,
      manifoldResonanceHz: 190,
      exhaustThroatHz: 640,
      highFreqRaspHz: 2900,
    },
    distortionAmount: 35,
    intakeIntensity: 0.42,
    exhaustIntensity: 0.50,
    mechanicalNoise: 0.035,
    lowFreqBodyGain: 0.55,
    highFreqCharacterGain: 0.75,
    accelerationResponse: 0.95,
    engineBrakingResponse: 0.85,
    compressionRatioDescription: '13.3:1 GT High-Rev Boxer-6',
  },

  // -------------------------------------------------------------
  // LAMBORGHINI REVUELTO V12 PROFILE
  // -------------------------------------------------------------
  lamborghini_v12: {
    id: 'lamborghini_v12',
    name: 'Lamborghini Revuelto 6.5L V12',
    tagline: 'Naturally aspirated 6.5L V12 screaming to 9,500 RPM',
    cylinders: 12,
    firingCharacteristics: 'v12',
    idleRpm: 850,
    redlineRpm: 9500,
    maxRpm: 9800,
    gearRatios: LAMBORGHINI_REVUELTO_DCT_PROFILE.gearRatios,
    finalDriveRatio: LAMBORGHINI_REVUELTO_DCT_PROFILE.finalDriveRatio,
    wheelCircumference: LAMBORGHINI_REVUELTO_DCT_PROFILE.wheelCircumference,
    transmissionProfile: LAMBORGHINI_REVUELTO_DCT_PROFILE,
    harmonicRatios: [0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0, 6.0],
    harmonicLevels: [0.35, 0.95, 0.40, 0.85, 0.50, 0.90, 0.75, 0.60, 0.45],
    harmonicDetunes: [-1, 0, 1, -1, 2, 0, -2, 1, 0],
    filterCharacteristics: {
      idleCutoffHz: 550,
      redlineCutoffHz: 6800,
      resonanceQ: 3.8,
      manifoldResonanceHz: 240,
      exhaustThroatHz: 780,
      highFreqRaspHz: 3800,
    },
    distortionAmount: 36,
    intakeIntensity: 0.45,
    exhaustIntensity: 0.60,
    mechanicalNoise: 0.03,
    lowFreqBodyGain: 0.45,
    highFreqCharacterGain: 0.85,
    accelerationResponse: 0.98,
    engineBrakingResponse: 0.90,
    compressionRatioDescription: '12.6:1 Naturally Aspirated 60-Deg V12 (825 CV @ 9,250 RPM)',
  },

  // -------------------------------------------------------------
  // 3 ARTIFICIAL BMW-INSPIRED PROFILES
  // -------------------------------------------------------------
  bmw_v10: {
    id: 'bmw_v10',
    name: 'BMW S85 5.0L High-Rev V10',
    tagline: 'F1-derived 8,250 RPM naturally aspirated howl',
    cylinders: 10,
    firingCharacteristics: 'v10',
    idleRpm: 800,
    redlineRpm: 8250,
    maxRpm: 8600,
    gearRatios: BMW_V10_SMG_ARTIFICIAL_PROFILE.gearRatios,
    finalDriveRatio: BMW_V10_SMG_ARTIFICIAL_PROFILE.finalDriveRatio,
    wheelCircumference: BMW_V10_SMG_ARTIFICIAL_PROFILE.wheelCircumference,
    transmissionProfile: BMW_V10_SMG_ARTIFICIAL_PROFILE,
    harmonicRatios: [0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 4.0, 5.0],
    harmonicLevels: [0.40, 0.90, 0.45, 0.85, 0.60, 0.80, 0.65, 0.40],
    harmonicDetunes: [-2, 0, 2, -1, 1, 0, -1, 1],
    filterCharacteristics: {
      idleCutoffHz: 500,
      redlineCutoffHz: 5600,
      resonanceQ: 3.4,
      manifoldResonanceHz: 210,
      exhaustThroatHz: 680,
      highFreqRaspHz: 3400,
    },
    distortionAmount: 34,
    intakeIntensity: 0.42,
    exhaustIntensity: 0.55,
    mechanicalNoise: 0.035,
    lowFreqBodyGain: 0.50,
    highFreqCharacterGain: 0.80,
    accelerationResponse: 0.95,
    engineBrakingResponse: 0.88,
    compressionRatioDescription: '12.0:1 90-Deg NA V10 (507 HP @ 7,750 RPM)',
  },

  bmw_i6_turbo: {
    id: 'bmw_i6_turbo',
    name: 'BMW S58 3.0L Twin-Turbo I6',
    tagline: 'Motorsport twin-turbo inline-6 rasp and midrange punch',
    cylinders: 6,
    firingCharacteristics: 'inline_even',
    idleRpm: 750,
    redlineRpm: 7200,
    maxRpm: 7500,
    gearRatios: BMW_I6_M_DCT_ARTIFICIAL_PROFILE.gearRatios,
    finalDriveRatio: BMW_I6_M_DCT_ARTIFICIAL_PROFILE.finalDriveRatio,
    wheelCircumference: BMW_I6_M_DCT_ARTIFICIAL_PROFILE.wheelCircumference,
    transmissionProfile: BMW_I6_M_DCT_ARTIFICIAL_PROFILE,
    harmonicRatios: [0.5, 1.0, 1.5, 2.0, 3.0, 4.5, 6.0],
    harmonicLevels: [0.60, 0.88, 0.35, 0.80, 0.65, 0.45, 0.30],
    harmonicDetunes: [-1, 0, 1, 0, -1, 1, 0],
    filterCharacteristics: {
      idleCutoffHz: 420,
      redlineCutoffHz: 3600,
      resonanceQ: 2.6,
      manifoldResonanceHz: 170,
      exhaustThroatHz: 500,
      highFreqRaspHz: 2300,
    },
    distortionAmount: 28,
    intakeIntensity: 0.40,
    exhaustIntensity: 0.46,
    mechanicalNoise: 0.04,
    lowFreqBodyGain: 0.70,
    highFreqCharacterGain: 0.55,
    accelerationResponse: 0.92,
    engineBrakingResponse: 0.80,
    compressionRatioDescription: '9.3:1 Twin-MonoScroll 3.0L Twin-Turbo (510 HP)',
  },

  bmw_na_v8: {
    id: 'bmw_na_v8',
    name: 'BMW S65 4.0L High-Rev V8',
    tagline: '8,400 RPM naturally aspirated M3 crossplane roar',
    cylinders: 8,
    firingCharacteristics: 'crossplane',
    idleRpm: 800,
    redlineRpm: 8400,
    maxRpm: 8700,
    gearRatios: BMW_S65_V8_DCT_ARTIFICIAL_PROFILE.gearRatios,
    finalDriveRatio: BMW_S65_V8_DCT_ARTIFICIAL_PROFILE.finalDriveRatio,
    wheelCircumference: BMW_S65_V8_DCT_ARTIFICIAL_PROFILE.wheelCircumference,
    transmissionProfile: BMW_S65_V8_DCT_ARTIFICIAL_PROFILE,
    harmonicRatios: [0.5, 1.0, 1.5, 2.0, 3.0, 4.0, 6.0, 8.0],
    harmonicLevels: [0.65, 0.90, 0.50, 0.85, 0.70, 0.55, 0.40, 0.25],
    harmonicDetunes: [-3, 0, 3, -2, 2, 0, -1, 1],
    filterCharacteristics: {
      idleCutoffHz: 460,
      redlineCutoffHz: 4900,
      resonanceQ: 3.2,
      manifoldResonanceHz: 185,
      exhaustThroatHz: 600,
      highFreqRaspHz: 2800,
    },
    distortionAmount: 32,
    intakeIntensity: 0.44,
    exhaustIntensity: 0.52,
    mechanicalNoise: 0.04,
    lowFreqBodyGain: 0.72,
    highFreqCharacterGain: 0.70,
    accelerationResponse: 0.95,
    engineBrakingResponse: 0.85,
    compressionRatioDescription: '12.0:1 High-Rev 90-Deg NA V8 (420 HP @ 8,300 RPM)',
  },
};

export const COMBUSTION_PROFILE_IDS: CombustionProfileId[] = [
  'v8',
  'v6',
  'i4',
  'flat6',
  'lamborghini_v12',
  'bmw_v10',
  'bmw_i6_turbo',
  'bmw_na_v8',
];

export const EV_PROFILE_IDS: EVProfileId[] = [
  'ev_cinematic',
  'ev_expressive',
  'ev_relax',
  'ev_sport',
  'ev_pulse',
  'ev_spatial',
];

export function isEVProfile(id: EngineProfileId | string): id is EVProfileId {
  return (
    id === 'ev_cinematic' ||
    id === 'ev_expressive' ||
    id === 'ev_relax' ||
    id === 'ev_sport' ||
    id === 'ev_pulse' ||
    id === 'ev_spatial' ||
    id === 'ev_futuristic'
  );
}

export function getEngineCategory(id: EngineProfileId | string): EngineCategory {
  return isEVProfile(id) ? 'EV' : 'COMBUSTION';
}

export const DEFAULT_ENGINE_PROFILE_ID: EngineProfileId = 'v8';
